import isISO8601 from 'validator/lib/isISO8601';
import { z } from 'zod';

import objectPassthrough from './objectPassthrough';
import { ISODateString } from './types';

type ActiveElement = {
  readonly updateTime: ISODateString;
  readonly isDeleted: false;
};

const ActiveElement = objectPassthrough({
  updateTime: z.string().refine((str) => isISO8601(str)),
  isDeleted: z.literal(false),
});

type Tombstone = {
  readonly updateTime: ISODateString;
  readonly isDeleted: true;
  readonly isArchived?: false;
};

const Tombstone = objectPassthrough({
  updateTime: z.string().refine((str) => isISO8601(str)),
  isDeleted: z.literal(true),
  isArchived: z.literal(false).optional(),
});

type ArchivedElement = {
  readonly updateTime: ISODateString;
  readonly isDeleted: true;
  readonly isArchived: true;
};

const ArchivedElement = objectPassthrough({
  updateTime: z.string().refine((str) => isISO8601(str)),
  isDeleted: z.literal(true),
  isArchived: z.literal(true),
});

type ActiveOrArchivedElement = ActiveElement | ArchivedElement;

const ActiveOrArchivedElement = z.union([ActiveElement, ArchivedElement]);

type HeymergeListValue<T> = T & ActiveElement;

type HeymergeArchivedListValue<T> = T & ArchivedElement;

type HeymergeListElement<T> = HeymergeListValue<T> | HeymergeArchivedListValue<T> | Tombstone;

interface HeymergeList<T> {
  readonly [id: string]: HeymergeListElement<T>;
}

// Exclude undefined, since the the list itself is optional if the key is optional in the spec,
// not the list elements.
type HeymergeSpecListElementType<T, K extends keyof T> = Exclude<T[K], undefined>;

type HeymergeVaultContent<T> = {
  readonly [K in keyof T]: HeymergeList<HeymergeSpecListElementType<T, K>>;
};

type HeymergeVaultContentExternal<T> = {
  readonly [K in keyof T]: { [k: string]: HeymergeSpecListElementType<T, K> };
};

export { ActiveElement, ArchivedElement, ActiveOrArchivedElement, Tombstone };

export type {
  HeymergeListValue,
  HeymergeArchivedListValue,
  HeymergeListElement,
  HeymergeList,
  HeymergeVaultContent,
  HeymergeVaultContentExternal,
  HeymergeSpecListElementType,
};
